import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SectionHeader from "@/components/base/SectionHeader";
import AnimeCard from "@/components/base/AnimeCard";
import NewsCard from "@/components/base/NewsCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { animes } from "@/mocks/animes";
import { newsList } from "@/mocks/news";
import { characters } from "@/mocks/characters";
import HeroCarousel from "./components/HeroCarousel";
import CategoryQuickLinks from "./components/CategoryQuickLinks";
import SiteIntro from "./components/SiteIntro";

export default function Home() {
  useDocumentMeta({
    title: "日漫动漫在线资源与最新资讯平台",
    description:
      "日漫网站专注于提供丰富的日本动画片资源和最新动漫资讯，涵盖热门动画推荐、角色介绍及行业动态，满足动漫爱好者的多样需求。",
    keywords: "日漫,日本动画,动漫资讯,动画推荐,动漫角色",
  });

  const heroItems = animes.slice(0, 4);
  const popular = [...animes].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const latestNews = newsList.slice(0, 3);
  const spotlight = [characters[0], characters[3], characters[9], characters[21]];

  return (
    <Layout>
      <HeroCarousel items={heroItems} />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 md:px-6 py-4 bg-background-100 border-b border-background-200 text-xs text-foreground-600">
        <span className="flex items-center gap-1.5">
          <i className="ri-calendar-line text-primary-500" />
          数据更新至 2026.10.15
        </span>
        <span className="hidden sm:inline text-background-300">|</span>
        <span className="flex items-center gap-1.5">
          <i className="ri-database-2-line text-primary-500" />
          已收录 {animes.length} 部动画 · {newsList.length} 篇资讯
        </span>
        <span className="hidden sm:inline text-background-300">|</span>
        <span className="flex items-center gap-1.5">
          <i className="ri-information-line text-primary-500" />
          本站为演示站点，不提供在线播放与下载
        </span>
      </div>

      <CategoryQuickLinks />

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <SectionHeader
          eyebrow="Trending Now"
          title="热门动画推荐"
          description="综合评分与讨论热度选出的近期人气作品，点击卡片即可查看剧情简介与角色资料。"
          action={{ label: "查看全部动画", to: "/category/all" }}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {popular.map((anime) => (
            <AnimeCard key={anime.id} anime={anime} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <SectionHeader
          eyebrow="Latest News"
          title="最新资讯精选"
          description="定档速报、制作访谈与行业观察，跟进日本动画圈的每一步动态。"
          action={{ label: "进入资讯中心", to: "/news" }}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {latestNews.map((news) => (
            <NewsCard key={news.id} news={news} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <SectionHeader
          eyebrow="Characters"
          title="角色聚焦"
          description="认识作品里最有魅力的角色，看看他们的性格、故事线与人物关系。"
          action={{ label: "全部角色资料", to: "/characters" }}
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {spotlight.map((character) => (
            <Link
              key={character.id}
              to={`/character/${character.id}`}
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="w-full aspect-square overflow-hidden bg-background-100">
                <img
                  src={character.avatar}
                  alt={`${character.name} 角色头像`}
                  title={`${character.name} 动漫角色介绍`}
                  className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {character.name}
                </h3>
                <p className="mt-0.5 text-xs text-foreground-500">{character.nameJa}</p>
                <p className="mt-2 text-xs text-primary-600 flex items-center gap-1">
                  <i className="ri-movie-2-line" />
                  {character.animeTitle}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">
                  {character.personality}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <SiteIntro />
    </Layout>
  );
}